import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MessagesSquare, UserX, Timer, Send, LayoutDashboard, ArrowRight } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { api } from "@/lib/api";
import { duration, listTime } from "@/lib/format";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth, useUsers } from "@/context/AuthContext";
import { LowRatingsPanel } from "@/components/LowRatingsPanel";
import { PhoneStatusBadge } from "@/components/PhoneStatusBadge";

const MetricCard = ({ label, value, icon: Icon, tone, testid, delay }) => (
  <div data-testid={testid} className="fade-up bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</span>
      <span className={`w-9 h-9 rounded-lg grid place-items-center ${tone}`}><Icon size={17} /></span>
    </div>
    <div data-testid={`${testid}-value`} className="mt-4 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
  </div>
);

const RecentList = () => {
  const { data } = useQuery({ queryKey: ["conversations", "all", null, ""], queryFn: () => api.get("/conversations").then((r) => r.data), refetchInterval: 5000 });
  const { data: users = [] } = useUsers();
  const byId = Object.fromEntries(users.map((u) => [u.id, u]));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 fade-up" style={{ animationDelay: "320ms" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">Conversazioni recenti</h3>
        <Link to="/inbox" data-testid="dashboard-open-inbox-link" className="text-xs font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1">Apri casella <ArrowRight size={13} /></Link>
      </div>
      <ul className="divide-y divide-slate-100">
        {(data?.items || []).slice(0, 6).map((c) => (
          <li key={c.id} className="py-2.5 flex items-center gap-3">
            <span className={`w-2 h-2 rounded-full ${c.unread ? "bg-[#25D366]" : "bg-slate-300"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate">{c.name || c.phone}</div>
              <div className="text-xs text-slate-500 truncate">{c.last_message}</div>
            </div>
            <span className="text-[11px] text-slate-400">{listTime(c.last_message_at)}</span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: byId[c.assigned_to]?.color || "#F97316" }}>
              {byId[c.assigned_to]?.name.split(" ")[0] || "Libera"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/dashboard/stats").then((r) => r.data), refetchInterval: 5000 });
  const trend = (data?.trend || []).map((t) => ({ ...t, label: format(new Date(t.date), "EEE d", { locale: it }) }));
  return (
    <div className="h-full flex flex-col">
      <PageHeader title="Dashboard" subtitle={`Bentornato, ${user?.name}`} icon={LayoutDashboard}><PhoneStatusBadge /></PageHeader>
      <div className="flex-1 overflow-y-auto thin-scroll p-4 md:p-8 space-y-6">
        {user?.role === "admin" && <LowRatingsPanel />}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-5">
          <MetricCard testid="metric-card-active-chats" label="Chat attive" value={data?.active_chats ?? "–"} icon={MessagesSquare} tone="bg-[#DCFCE7] text-[#15803D]" delay={0} />
          <MetricCard testid="metric-card-unassigned-chats" label="Non assegnate" value={data?.unassigned_chats ?? "–"} icon={UserX} tone="bg-orange-100 text-orange-700" delay={60} />
          <MetricCard testid="metric-card-avg-response" label="Tempo medio risposta" value={data ? duration(data.avg_response_seconds) : "–"} icon={Timer} tone="bg-slate-100 text-slate-700" delay={120} />
          <MetricCard testid="metric-card-sent-today" label="Inviati oggi" value={data?.sent_today ?? "–"} icon={Send} tone="bg-blue-50 text-blue-700" delay={180} />
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5">
          <div data-testid="message-trend-chart" className="min-w-0 bg-white border border-slate-200 rounded-xl p-5 fade-up" style={{ animationDelay: "240ms" }}>
            <div className="flex flex-wrap gap-2 items-center justify-between mb-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Volume messaggi giornaliero</h3>
                <p className="text-xs text-slate-500">Ultimi 7 giorni</p>
              </div>
              <div className="flex gap-4 text-xs text-slate-600">
                <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-[#25D366]" />Inviati</span>
                <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-[#0F172A]" />Ricevuti</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 10, right: 6, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#25D366" stopOpacity={0.35} /><stop offset="100%" stopColor="#25D366" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="inviati" stroke="#25D366" strokeWidth={2.5} fill="url(#gOut)" />
                  <Area type="monotone" dataKey="ricevuti" stroke="#0F172A" strokeWidth={2} fill="transparent" strokeDasharray="4 3" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <RecentList />
        </div>
      </div>
    </div>
  );
}
